import {
  ApiRequestsContext,
  RemoteTypeMappings
} from '@tableau/data-catalog-shared';
import { removeNulls } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogType, DialogWidget } from '@tableau/widgets';
import * as React from 'react';
import { Query } from 'react-apollo';
import { Messages } from '../../../gen-src/Localize';
import {
  ColumnDetailsQuery,
  ColumnDetailsQueryVariables
} from '../../../gen-src/queries/ColumnDetailsQuery';
import { ColumnDetailsQueryGql } from '../../queries/ColumnDetailsQueryGql';
import { RemoteTypeIcon } from '../datagrid/cellFormatters/RemoteTypeIcon';
import { renderQueryResult } from '../renderQueryResult';
import { getRowHeight } from '../sharedDisplayGrid/LinksDisplayGrid';
import {
  ColumnAndFieldDetailsLabel,
  ColumnAndFieldDetailsProperty,
  ColumnDetailsColumnName,
  ColumnDetailsPane,
  ColumnDetailsProperties,
  ColumnDetailsTypeRow,
  ColumnFieldsWrapper,
  columnFieldsWrapperMaxRows,
} from '../style/ColumnAndFieldDetailsStyles';
import { ColumnFieldsQueryGrid } from './ColumnFieldsQueryGrid';
import { ColumnFlowsQueryGrid } from './ColumnFlowsQueryGrid';
import { ColumnInlineDescriptionEditor } from './ColumnInlineDescriptionEditor';
import { ColumnInlineTagEditor } from './ColumnInlineTagEditor';

const dialogWidth = 600;


type ColumnDetailsDialogProps = {
  readonly columnId: string;
  readonly onClose: () => void;
  readonly afterDescriptionChange: () => void;
  readonly canSetDescriptionAndTags: boolean;
  readonly columns: ColumnDetailsQuery['columns'];
};

function ColumnDetailsDialog(props: ColumnDetailsDialogProps): JSX.Element | null {
  const column = props.columns && props.columns[0];

  if (!column) {
    return null;
  }

  const tags = removeNulls(column.tags.map(tag => tag.name));
  const fieldsCount = column.referencedByFieldsConnection?.totalCount ?? null;
  const flowsCount = column.referencedByFlowColumnInputFieldConnection?.totalCount ?? null;

  return (
    <DialogWidget
      testId='column-details'
      title={Messages.ColumnDetails()}
      type={DialogType.NormalWithTitleBar}
      positioningBehavior={DialogPositioningBehavior.Centered}
      showCloseButton={true}
      showFooter={false}
      isModal={true}
      onCloseRequest={props.onClose}
      handleGlassClick={props.onClose}
      dialogWidth={dialogWidth}
    >
      <ColumnDetailsPane>
        <ColumnDetailsColumnName>{column.name}</ColumnDetailsColumnName>
        <ColumnDetailsTypeRow>
          <RemoteTypeIcon remoteType={column.remoteType} />
          {RemoteTypeMappings[column.remoteType]()}
        </ColumnDetailsTypeRow>
        <ColumnDetailsProperties>
          <ColumnInlineDescriptionEditor
            columnVizportalId={column.vizportalId}
            description={column.description || ''}
            afterDescriptionChange={props.afterDescriptionChange}
            canEditDescription={props.canSetDescriptionAndTags}
          />
          <ColumnInlineTagEditor
            columnLuid={column.luid}
            tags={tags}
            onUpdate={props.afterDescriptionChange}
            canEditTags={props.canSetDescriptionAndTags}
          />
          <ColumnAndFieldDetailsProperty>
            <ColumnAndFieldDetailsLabel>{Messages.UsedByFields()}</ColumnAndFieldDetailsLabel>
            <ColumnFieldsWrapper style={{ height: getRowHeight(fieldsCount, columnFieldsWrapperMaxRows) }}>
              <ColumnFieldsQueryGrid
                columnId={props.columnId}
                rowCount={fieldsCount}
              />
            </ColumnFieldsWrapper>
          </ColumnAndFieldDetailsProperty>
          <ColumnAndFieldDetailsProperty>
            <ColumnAndFieldDetailsLabel>{Messages.UsedByFlows()}</ColumnAndFieldDetailsLabel>
            <ColumnFieldsWrapper style={{ height: getRowHeight(flowsCount, columnFieldsWrapperMaxRows) }}>
              <ColumnFlowsQueryGrid
                columnId={props.columnId}
                rowCount={flowsCount}
              />
            </ColumnFieldsWrapper>
          </ColumnAndFieldDetailsProperty>
        </ColumnDetailsProperties>
      </ColumnDetailsPane>
    </DialogWidget>
  );
}


export type ColumnDetailsDialogQueryWrapperProps = {
  readonly columnId: string;
  readonly tableVizportalId: string | null;
  readonly onClose: () => void;
  readonly afterDescriptionChange: () => void;
};

export function ColumnDetailsDialogQueryWrapper(props: ColumnDetailsDialogQueryWrapperProps): JSX.Element {
  const apiRequests = React.useContext(ApiRequestsContext);
  const [canSetDescriptionAndTags, setCanSetDescriptionAndTags] = React.useState(false);

  React.useEffect(
    () => {
      apiRequests.canUserSetColumnDescription({ tableVizportalId: props.tableVizportalId ?? '' })
        .then(r => setCanSetDescriptionAndTags(r))
        .catch(() => { return; });
    },
    [apiRequests, props.tableVizportalId]
  );

  return (
    <Query<ColumnDetailsQuery, ColumnDetailsQueryVariables>
      query={ColumnDetailsQueryGql}
      variables={{ columnId: props.columnId }}
    >
      {result => renderQueryResult(result, {
        renderData: data => (
          <ColumnDetailsDialog
            columnId={props.columnId}
            onClose={props.onClose}
            afterDescriptionChange={props.afterDescriptionChange}
            columns={data.columns}
            canSetDescriptionAndTags={canSetDescriptionAndTags}
          />
        )
      })}
    </Query>
  );
}
